import { selectCustomerType, selectCustomerFirstName } from '../../../../../../store/selectors/customerProfile'
import { useState, useMemo } from 'react'
import * as Sentry from '@sentry/browser'
import { useSelector } from 'react-redux'
import { useIntl, defineMessages } from 'react-intl'
import { MessageTopicKey } from '../../../../../../api/gqlaxy/gql/generated/graphql'
import { useSendListingMessage } from '../../../../../../hooks/listings/useSendListingMessage'
import {
  Avatar,
  Box,
  Button,
  ButtonGroup,
  ComponentTrackingType,
  Flex,
  Heading,
  Icon,
  Text
} from '@homebotapp/hb-react-component-catalog'
import { selectSponsorshipFirstName, selectSponsorshipPhotoUri } from '../../../../../../store/selectors/sponsorship'
import ConfettiTarget from '../../../../../shared/Confetti/ConfettiTarget'
import { TrackingEventConstants } from '../../../../../../constants/tracking'
import { TrackingEventHelpers } from '../../../../../../helpers/tracking'
import { useTrackingContext } from '../../../../../../providers/tracking'
import { createBuyerMessage } from '../../../../../../api/mikasa/requests'
import { CUSTOMER_MESSAGES } from '../../../../../../constants/customerMessages'
import { GenericVerificationDirectMessage } from '../../../../GenericVerificationDirectMessage/GenericVerificationDirectMessage'
import { useVerificationFlowEnabled } from '../../../../../../hooks/useVerificationFlowEnabled'
import { useCustomerBranding } from '../../../../../../hooks/homebotNetwork/usePreferredAgentOrCustomer'

export const MSG = defineMessages({
  head: {
    id: 'EstimatedMonthlyPaymentBreakdownCustomerDM.head',
    defaultMessage: 'Want to talk numbers?'
  },
  prequal: {
    id: 'EstimatedMonthlyPaymentBreakdownCustomerDM.prequal',
    defaultMessage: 'Get pre-qualified'
  },
  requestPrequal: {
    id: 'EstimatedMonthlyPaymentBreakdownCustomerDM.requestPrequal',
    defaultMessage: 'Request pre-qualification'
  },
  confirm: {
    id: 'EstimatedMonthlyPaymentBreakdownCustomerDM.confirm',
    defaultMessage: 'Confirm request'
  },
  cancel: {
    id: 'EstimatedMonthlyPaymentBreakdownCustomerDM.cancel',
    defaultMessage: 'Cancel'
  },
  body: {
    id: 'EstimatedMonthlyPaymentBreakdownCustomerDM.body',
    defaultMessage: 'Confirm what your monthly payment might look like for'
  },
  success: {
    id: 'EstimatedMonthlyPaymentBreakdownCustomerDM.success',
    defaultMessage: 'Request sent!'
  },
  fail: {
    id: 'EstimatedMonthlyPaymentBreakdownCustomerDM.fail',
    defaultMessage: 'Request failed. Try again?'
  },
  notifies: {
    id: 'EstimatedMonthlyPaymentBreakdownCustomerDM.notifies',
    defaultMessage:
      'This notifies {firstName} that you are interested in getting pre-qualified. There is no obligation moving forward.'
  }
})

export interface EstimatedMonthlyPaymentBreakdownCustomerDMProps {
  address?: string
  buyerId?: string
  listingId?: string
  showModal?: boolean
  customMessage?: string
  tracking?: ComponentTrackingType
}

enum DMStatus {
  Initial = 'initial',
  Confirmation = 'confirmation',
  Success = 'success',
  Fail = 'fail'
}

// this let's the confetti animation finish before resetting the state
const debounce = (func, delay) => {
  let timer
  return function (...args) {
    clearTimeout(timer)
    timer = setTimeout(() => {
      // @ts-ignore
      func.apply(this, args)
    }, delay)
  }
}

const animationTimer = 200

export const EstimatedMonthlyPaymentBreakdownCustomerDM = ({
  address,
  buyerId,
  listingId,
  showModal,
  customMessage,
  tracking
}: EstimatedMonthlyPaymentBreakdownCustomerDMProps) => {
  const intl = useIntl()
  const isVerificationFlowEnabled = useVerificationFlowEnabled()
  const branding = useCustomerBranding('LoanOfficer')
  const customerPhoto = branding?.photoUri
  const sponsorPhoto = useSelector(selectSponsorshipPhotoUri)

  const customerFirstName = useSelector(selectCustomerFirstName)
  const sponsorFirstName = useSelector(selectSponsorshipFirstName)

  // Memoize header section to prevent Avatar re-renders
  const headerSection = useMemo(
    () => (
      <>
        <Avatar src={sponsorPhoto ?? customerPhoto} mb={3} />
        <Heading mb={1} as='h4' size={['sm', 'md']} fontWeight='700'>
          {intl.formatMessage(MSG.head)}
        </Heading>
        {address && (
          <Text size={['xs', 'sm']} textAlign='center' mb={4} maxW={80}>
            {intl.formatMessage(MSG.body)}{' '}
            <Text size='inherit' as='span' fontWeight='semibold' color='neutral.900'>
              {address}
            </Text>
          </Text>
        )}
      </>
    ),
    [sponsorPhoto, customerPhoto, intl, address]
  )

  // Memoize footer section
  const footerSection = useMemo(
    () => (
      <Text size='2xs' textAlign='center' mt={4}>
        {intl.formatMessage(MSG.notifies, { firstName: sponsorFirstName ?? customerFirstName })}
      </Text>
    ),
    [intl, sponsorFirstName, customerFirstName]
  )

  return (
    <Flex mt={6} mx='auto' direction='column' align='center' maxW='25rem' w='100%'>
      {headerSection}

      {isVerificationFlowEnabled ? (
        <VerificationFlowExperience showModal={showModal} customMessage={customMessage} tracking={tracking} />
      ) : (
        <StandardExperience buyerId={buyerId} listingId={listingId} customMessage={customMessage} tracking={tracking} />
      )}

      {footerSection}
    </Flex>
  )
}

// New verification flow experience
const VerificationFlowExperience = ({
  customMessage,
  tracking,
  showModal
}: {
  customMessage?: string
  tracking?: ComponentTrackingType
  showModal?: boolean
}) => {
  const intl = useIntl()

  return (
    <GenericVerificationDirectMessage
      defaultTopicKey={CUSTOMER_MESSAGES.BUYER_PREQUAL_REQUEST.topicKey}
      verifiedTopicKey={CUSTOMER_MESSAGES.HBN_BUYER_PREQUAL_REQUEST.topicKey}
      message={customMessage ?? CUSTOMER_MESSAGES.HBN_BUYER_PREQUAL_REQUEST.message.defaultMessage}
      buttonText={intl.formatMessage(MSG.requestPrequal)}
      partnerType='LoanOfficer'
      includePrequal={false}
      showModal={showModal}
      buttonProps={{
        leftIcon: <Icon name='message' />,
        colorScheme: 'brand',
        variant: 'solid',
        size: 'sm',
        showAvatar: false,
        tracking: tracking ?? {
          guid: 'vwwBw2W6tcrTGacSTM2QqL',
          ui_context: 'Estimated Monthly Payment Breakdown Get Pre-qualified',
          descriptor: 'Get pre-qualified verification flow'
        }
      }}
    />
  )
}

// Standard animated button experience
interface StandardExperienceProps {
  buyerId?: string
  listingId?: string
  customMessage?: string
  tracking?: ComponentTrackingType
}

const StandardExperience = ({ buyerId, listingId, customMessage, tracking }: StandardExperienceProps) => {
  const intl = useIntl()
  const { trackingClient } = useTrackingContext()
  const customerType = useSelector(selectCustomerType)
  const [isLoading, setIsLoading] = useState(false)
  const [dmStatus, setDmStatus] = useState<DMStatus>(DMStatus.Initial)
  const [showMiniConfetti, setShowMiniConfetti] = useState(false)
  const [showMiniConfettiAlt, setShowMiniConfettiAlt] = useState(false)
  const [confettiCounter, setConfettiCounter] = useState(0)
  const { mutateAsync: sendListingMessage } = useSendListingMessage()

  const handleFinalStatus = () => {
    if (dmStatus === DMStatus.Fail) {
      setDmStatus(DMStatus.Initial)
    }

    if (dmStatus === DMStatus.Success) {
      setConfettiCounter(prevCounter => prevCounter + 1)
      const actionIndex = confettiCounter % 4

      switch (actionIndex) {
        case 0:
          setShowMiniConfetti(true)
          setShowMiniConfettiAlt(false)
          break
        case 1:
          setShowMiniConfettiAlt(true)
          break
        case 2:
          setShowMiniConfetti(true)
          break
        case 3:
          setShowMiniConfettiAlt(true)
          setTimeout(() => {
            if (confettiCounter % 4 === 3) {
              setShowMiniConfetti(false)
              setShowMiniConfettiAlt(false)
            }
          }, animationTimer)
          break
      }
    }
  }

  const debouncedHandleFinalStatus = debounce(handleFinalStatus, animationTimer)

  const handleSubmit = async () => {
    setIsLoading(true)

    trackingClient?.clientRequestedInfo({
      request_type: TrackingEventConstants.CLIENT_REQUESTED_PREQUAL,
      customer_type: TrackingEventHelpers.getTrackingCustomerType(customerType),
      ui_context: 'Estimated Monthly Payment Breakdown Get Pre-qualified'
    })

    try {
      if (listingId) {
        await sendListingMessage({
          input: {
            listingId: listingId,
            topicKey: MessageTopicKey.ListingsPrequalRequest,
            message: customMessage ?? 'I want to get pre-qualified for this home.'
          }
        })
      } else if (buyerId) {
        await createBuyerMessage(
          buyerId,
          {
            metadata: null,
            topic: CUSTOMER_MESSAGES.BUYER_PREQUAL_REQUEST.topic,
            topicKey: CUSTOMER_MESSAGES.BUYER_PREQUAL_REQUEST.topicKey,
            message: customMessage ?? CUSTOMER_MESSAGES.BUYER_PREQUAL_REQUEST.message.defaultMessage
          },
          trackingClient
        )
      }
      setDmStatus(DMStatus.Success)
    } catch (err: any) {
      setDmStatus(DMStatus.Fail)
      Sentry.captureMessage('DM failed to send', {
        extra: {
          error: err.message || '',
          requestData: err.config?.data || {},
          requestErrors: Object.values(err.response?.data?.errors) || []
        }
      })
    } finally {
      setTimeout(() => {
        setIsLoading(false)
      }, 300)
    }
  }

  return (
    <>
      {/* @ts-ignore */}
      <ConfettiTarget go={dmStatus === DMStatus.Success} numberOfPieces={40}>
        {/* @ts-ignore */}
        <ConfettiTarget go={showMiniConfetti} numberOfPieces={32}>
          {/* @ts-ignore */}
          <ConfettiTarget go={showMiniConfettiAlt} numberOfPieces={72}>
            <Box position='relative' mb={0} overflow='hidden' w={['80%', '100%']} mx='auto'>
              <Flex direction='column' gap={4} transition={'.15s ease-in-out transform'}>
                <Flex direction='column' gap={4}>
                  {dmStatus === DMStatus.Initial && (
                    <Button
                      py='8px'
                      width='100%'
                      variant={'solid'}
                      opacity={'100% !important'}
                      colorScheme='brand'
                      onClick={() => setDmStatus(DMStatus.Confirmation)}
                    >
                      {intl.formatMessage(MSG.prequal)}
                    </Button>
                  )}
                  {dmStatus === DMStatus.Confirmation && (
                    <ButtonGroup width='100%'>
                      {!isLoading && (
                        <Button
                          w='auto'
                          transition={'.2s ease-in-out all'}
                          px={12}
                          onClick={() => setDmStatus(DMStatus.Initial)}
                        >
                          {intl.formatMessage(MSG.cancel)}
                        </Button>
                      )}
                      <Button
                        w='100%'
                        isLoading={isLoading}
                        onClick={() => handleSubmit()}
                        colorScheme='brand'
                        tracking={
                          tracking ?? {
                            guid: 'mGSiTskZRWbDoZLHvzJPx8',
                            ui_context: 'Estimated Monthly Payment Breakdown Get Pre-qualified',
                            descriptor: 'Confirm pre-qualification request'
                          }
                        }
                      >
                        {intl.formatMessage(MSG.confirm)}
                      </Button>
                    </ButtonGroup>
                  )}
                </Flex>
                {(dmStatus === DMStatus.Success || dmStatus === DMStatus.Fail) && (
                  <Button
                    w='100%'
                    variant='outline'
                    colorScheme='secondary'
                    bg='neutral.50'
                    mt={-4}
                    leftIcon={
                      dmStatus === DMStatus.Success ? <Icon name='check-circle-fa' width={3} height={3} /> : undefined
                    }
                    onClick={() => debouncedHandleFinalStatus()}
                  >
                    {dmStatus === DMStatus.Success && intl.formatMessage(MSG.success)}
                    {dmStatus === DMStatus.Fail && intl.formatMessage(MSG.fail)}
                  </Button>
                )}
              </Flex>
            </Box>
          </ConfettiTarget>
        </ConfettiTarget>
      </ConfettiTarget>
      <Text
        style={{ transform: dmStatus === DMStatus.Fail ? 'translateY(0%)' : 'translateY(-100%)' }}
        zIndex={dmStatus === DMStatus.Fail ? 1 : -1}
        opacity={dmStatus === DMStatus.Fail ? '100%' : 0}
        mb={dmStatus === DMStatus.Fail ? 2 : -2}
        mt={dmStatus === DMStatus.Fail ? 0 : -2}
        transition='.2s ease-in-out all'
        as='span'
        size='sm'
        fontWeight='semibold'
        color='warning.700'
      >
        We failed to send the message
      </Text>
    </>
  )
}
